import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { footerData, companyInfo } from '../data/mock';
import { useFooterContent, useContactContent } from '../hooks/useCMSContent';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [ref, isVisible] = useScrollAnimation(0.1);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use CMS content with fallback to mock data
  const { footer: cmsFooter } = useFooterContent();
  const { contact: cmsContact } = useContactContent();
  
  const footer = cmsFooter || footerData;
  const contact = cmsContact || {};
  
  // Use CMS contact info or fallback
  const displayEmail = contact.email || companyInfo.email;
  const displayPhone = contact.phone || companyInfo.phone;
  const displayAddress = contact.address || companyInfo.fullAddress;

  // Handle smooth scroll for hash links
  const handleHashClick = (e, hash) => {
    e.preventDefault();
    const targetId = hash.replace('/#', '');
    
    if (location.pathname === '/') {
      // Already on home page - scroll directly
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Navigate to home and then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <footer ref={ref} className="bg-[#050505] text-white border-t border-white/[0.06]">
      <div className="max-w-6xl mx-auto px-6">
        {/* Main Footer */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          className="py-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
            {/* Logo Column */}
            <motion.div variants={itemVariants} className="col-span-2 md:col-span-4">
              <motion.div
                className="flex items-center gap-3 mb-6"
                whileHover={{ scale: 1.05 }}
              >
                <img
                  src={companyInfo.logo}
                  alt="Agentic Atoms Logo"
                  className="w-14 h-14 rounded-full object-cover bg-white"
                />
                <span className="text-white font-semibold text-lg">Agentic Atoms</span>
              </motion.div>
              <p className="text-[#71717A] text-sm leading-relaxed mb-6">
                {footer.tagline || footerData.tagline}
              </p>
              
              {/* Contact Info - Icons with text */}
              <div className="space-y-2 text-sm text-[#71717A]">
                <a href={`mailto:${displayEmail}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span>{displayEmail}</span>
                </a>
                <a href={`tel:${displayPhone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>{displayPhone}</span>
                </a>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{displayAddress}</span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3 mt-6">
                <a
                  href="https://www.linkedin.com/company/agentic-atoms/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-[#FF6B00]/15 hover:border-[#FF6B00]/30 hover:text-[#FF6B00] text-[#A1A1AA] flex items-center justify-center transition-all duration-300"
                  data-testid="footer-linkedin"
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon />
                </a>
                <a
                  href="https://www.instagram.com/agenticatoms/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-[#FF6B00]/15 hover:border-[#FF6B00]/30 hover:text-[#FF6B00] text-[#A1A1AA] flex items-center justify-center transition-all duration-300"
                  data-testid="footer-instagram"
                  aria-label="Instagram"
                >
                  <InstagramIcon />
                </a>
              </div>
            </motion.div>

            {/* Links Columns */}
            {Object.entries(footerData.links).map(([category, links], categoryIndex) => (
              <motion.div key={category} variants={itemVariants} className="col-span-1 md:col-span-2">
                <h4 className="text-white font-medium text-sm mb-5">{category}</h4>
                <ul className="space-y-3">
                  {links.map((link, linkIndex) => {
                    // Determine the correct link path
                    const getLinkPath = (linkText) => {
                      // Company pages
                      if (linkText === 'Careers') return '/careers';
                      if (linkText === 'Blog') return '/blog';
                      if (linkText === 'Case Studies' || linkText === 'Documentation' || linkText === 'Support') return '/resources';
                      if (linkText === 'About Us') return '/#about';
                      if (linkText === 'Contact') return '/#contact';
                      return '/#services';
                    };
                    const path = getLinkPath(link);
                    const isHashLink = path.startsWith('/#');

                    return (
                      <motion.li
                        key={link}
                        initial={{ opacity: 0, x: -10 }}
                        animate={isVisible ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.3 + categoryIndex * 0.1 + linkIndex * 0.05 }}
                      >
                        {isHashLink ? (
                          <motion.a
                            href={path}
                            onClick={(e) => handleHashClick(e, path)}
                            className="text-[#71717A] hover:text-white text-sm transition-colors duration-200 inline-block cursor-pointer"
                            whileHover={{ x: 3 }}
                          >
                            {link}
                          </motion.a>
                        ) : (
                          <Link to={path}>
                            <motion.span
                              className="text-[#71717A] hover:text-white text-sm transition-colors duration-200 inline-block cursor-pointer"
                              whileHover={{ x: 3 }}
                            >
                              {link}
                            </motion.span>
                          </Link>
                        )}
                      </motion.li>
                    );
                  })}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="py-8 border-t border-gray-700 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <p className="text-[#71717A] text-sm">
            © {currentYear} {footer.copyright || 'Agentic Atoms. All rights reserved.'}
          </p>
          <div className="flex items-center gap-8">
            {[
              { name: 'Privacy', path: '/privacy' },
              { name: 'Terms', path: '/terms' },
              { name: 'Cookies', path: '/cookies' },
              { name: 'Accessibility', path: '/accessibility' }
            ].map((item, index) => (
              <Link
                key={item.name}
                to={item.path}
                className="text-[#71717A] hover:text-white text-sm transition-colors duration-200"
              >
                <motion.span
                  whileHover={{ y: -2 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 1 + index * 0.1 }}
                  className="inline-block"
                >
                  {item.name}
                </motion.span>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
